import {
  addFarmer,
  deleteFarmer,
  getFarmer,
  updateFarmer,
} from '@/adapters/master';
import { IFrm, IFrmRes, IApiResponse } from '@/interfaces/master';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Flex,
  notification,
  Radio,
  Table,
  TableColumnsType,
  Input,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { uniqueId } from 'lodash';

interface IBatchRejection {
  status: 'rejected' | 'fulfilled';
  reason?: { errors?: { errorMessage?: string } };
}

type FarmerBatchData = Partial<IFrm>[];

const getErrorMessage = (r: unknown): string => {
  if (r instanceof Error) return r.message;
  if (typeof r === 'object' && r !== null && 'message' in r)
    return (r as { message: string }).message;
  return 'เกิดข้อผิดพลาดในการดำเนินการ';
};

const FarmerPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<IFrm[]>([]);
  const [tab, setTab] = useState<'create' | 'update'>('create');
  const [selected, setSelected] = useState<IFrm[]>([]);
  const [searchText, setSearchText] = useState('');

  const { data, refetch, isFetching } = useQuery<IFrmRes, Error>({
    queryKey: ['get-famer'],
    queryFn: getFarmer,
    retry: 0,
  });

  const notifyBatchErrors = (
    title: string,
    batchResults: IBatchRejection[],
    originalData: FarmerBatchData,
  ) => {
    const errors: string[] = [];
    if (Array.isArray(batchResults)) {
      batchResults.forEach((result, index) => {
        if (result.status === 'rejected') {
          const failedCode = originalData[index]?.FRM_CODE || `Index: ${index}`;
          errors.push(
            `${failedCode}: ${result.reason?.errors?.errorMessage || 'ไม่ทราบสาเหตุ'}`,
          );
        }
      });
    }
    if (errors.length > 0) {
      notification.error({
        message: title,
        style: { width: '500px' },
        description: (
          <ul
            style={{
              paddingLeft: '15px',
              margin: 0,
              maxHeight: '250px',
              overflowY: 'auto',
            }}
          >
            {errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        ),
        duration: 8,
      });
      return true;
    }
    return false;
  };

  const actionFarmerMutation = useMutation({
    mutationFn: (payload: FarmerBatchData) =>
      tab === 'create' ? addFarmer(payload) : updateFarmer(payload),
    onSuccess: (res, variables) => {
      if (
        !notifyBatchErrors(
          tab === 'create'
            ? 'Sync Farmer ไม่สำเร็จ'
            : 'Update Farmer ไม่สำเร็จ',
          res.data as IBatchRejection[],
          variables,
        )
      ) {
        notification.success({
          message:
            tab === 'create' ? 'Sync Farmer สำเร็จ' : 'Update Farmer สำเร็จ',
        });
      }
      refetch();
    },
    onError: r => notification.error({ message: getErrorMessage(r) }),
  });

  const actionDeleteFarmer = useMutation({
    mutationFn: (payload: FarmerBatchData) => deleteFarmer(payload),
    onSuccess: (res, variables) => {
      if (
        !notifyBatchErrors(
          'ลบข้อมูลไม่สำเร็จ',
          res.data as IBatchRejection[],
          variables,
        )
      ) {
        notification.success({ message: 'Delete Farmer สำเร็จ' });
      }
      refetch();
    },
    onError: r => notification.error({ message: getErrorMessage(r) }),
  });

  const filteredData = useMemo(() => {
    if (!searchText) return selected;
    const lowerSearch = searchText.toLowerCase();
    return selected.filter(
      item =>
        item.FRM_CODE?.toLowerCase().includes(lowerSearch) ||
        item.FRM_FNAME?.toLowerCase().includes(lowerSearch) ||
        item.FRM_LNAME?.toLowerCase().includes(lowerSearch),
    );
  }, [selected, searchText]);

  const columns: TableColumnsType<IFrm> = useMemo(
    () => [
      {
        title: 'ลำดับ',
        width: 60,
        align: 'center',
        render: (_, __, i) => <b>{i + 1}</b>,
      },
      {
        title: 'รหัส',
        dataIndex: 'FRM_CODE',
        width: 120,
        sorter: (a, b) => (a.FRM_CODE || '').localeCompare(b.FRM_CODE || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FRM_CODE).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FRM_CODE === value,
        filterSearch: true,
      },
      {
        title: 'ชื่อ',
        dataIndex: 'FRM_FNAME',
        width: 150,
        sorter: (a, b) => (a.FRM_FNAME || '').localeCompare(b.FRM_FNAME || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FRM_FNAME).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FRM_FNAME === value,
        filterSearch: true,
      },
      {
        title: 'นามสกุล',
        dataIndex: 'FRM_LNAME',
        width: 150,
        sorter: (a, b) => (a.FRM_LNAME || '').localeCompare(b.FRM_LNAME || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FRM_LNAME).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FRM_LNAME === value,
        filterSearch: true,
      },
      {
        title: 'ตำบล(ที่อยู่)',
        dataIndex: 'FRM_LINE3',
        width: 140,
        sorter: (a, b) => (a.FRM_LINE3 || '').localeCompare(b.FRM_LINE3 || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FRM_LINE3).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FRM_LINE3 === value,
        filterSearch: true,
      },
      {
        title: 'อำเภอ(ที่อยู่)',
        dataIndex: 'FRM_LINE2',
        width: 140,
        sorter: (a, b) => (a.FRM_LINE2 || '').localeCompare(b.FRM_LINE2 || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FRM_LINE2).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FRM_LINE2 === value,
        filterSearch: true,
      },
      {
        title: 'จังหวัด(ที่อยู่)',
        dataIndex: 'FRM_PROVICE',
        width: 140,
        sorter: (a, b) =>
          (a.FRM_PROVICE || '').localeCompare(b.FRM_PROVICE || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FRM_PROVICE).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FRM_PROVICE === value,
        filterSearch: true,
      },
    ],
    [selected],
  );

  useEffect(() => {
    const listMap = {
      create: data?.insert,
      update: data?.update,
      match: data?.match,
    };
    const currentList = listMap[tab] || [];
    setSelected(
      currentList.map(c => ({ ...c, _key: c._key || uniqueId('frm-') })),
    );
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, [tab, data]);

  const isPending =
    actionFarmerMutation.isPending || actionDeleteFarmer.isPending;

  return (
    <div style={{ padding: '20px' }}>
      <Flex justify="space-between" align="center" className="mb-3">
        <Radio.Group
          value={tab}
          onChange={e => setTab(e.target.value)}
          buttonStyle="solid"
        >
          <Radio.Button value="create">เพิ่มข้อมูล</Radio.Button>
          <Radio.Button value="update">แก้ไข/ลบ</Radio.Button>
        </Radio.Group>
        <Flex gap={12}>
          <Input.Search
            placeholder="ค้นหา (รหัส/ชื่อ/นามสกุล)"
            allowClear
            onSearch={setSearchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 300 }}
          />
          {tab === 'update' && (
            <button
              type="button"
              className="btn btn-outline-danger"
              disabled={!selectedRowKeys.length || isPending}
              onClick={() => actionDeleteFarmer.mutate(selectedRows)}
            >
              ลบข้อมูล Farmer
            </button>
          )}
          <button
            type="button"
            className="btn btn-success"
            disabled={!selectedRowKeys.length || isPending}
            onClick={() => actionFarmerMutation.mutate(selectedRows)}
          >
            {isPending
              ? 'กำลังดำเนินการ...'
              : tab === 'create'
                ? 'ส่งข้อมูลไป OTOD >'
                : 'แก้ไขข้อมูลที่ OTOD >'}
          </button>
        </Flex>
      </Flex>
      <Table<IFrm>
        rowSelection={{
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
          },
        }}
        columns={columns}
        dataSource={filteredData}
        loading={isFetching}
        size="small"
        bordered
        rowKey="_key"
        pagination={false}
        scroll={{ x: 1200 }}
        sticky={{ offsetHeader: 0 }}
      />
    </div>
  );
};

export default FarmerPage;
